'use client';

import { ScrapersMediumIcon } from '@smartproxy/decoro/v2/icons';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';

import './ScrapingHero.css';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { track } from '@/lib/tracker';
import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import Button from '@/components/atoms/Button';
import { passButtonIcons } from '@/components/atoms/Button/utils';
import { Text } from '@/components/atoms/Text';
import type {
  ScrapingCardProps,
  ScrapingHeroAction,
  ScrapingHeroResponse,
} from '@/components/bento/ScrapingPlayground/types';
import { SvgIcon } from '@/components/SvgIcon';

import {
  HERO_WIDGET_EXAMPLE_BADGE,
  HERO_WIDGET_EXAMPLE_BADGE_SELECTED,
  HERO_WIDGET_ICON_WRAPPER,
} from '@/features/HomepageHero/components/heroWidgetStyles';
import { WidgetGlassOverlay } from '@/features/HomepageHero/components/WidgetGlassOverlay';
import { sendGAEvent } from '@/integrations/trackingScripts';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';
import { gradientDecodoBorderStyling } from '@/styles/gradient';

import { scrape } from './api-utils/scrape';
import {
  SCRAPING_HERO_SCRAPE_FAILED_EVENT,
  SCRAPING_HERO_SCRAPE_JOB_STARTED_EVENT,
  SCRAPING_HERO_SCRAPE_SUCCEEDED_EVENT,
  SCRAPING_HERO_TAB_IDS,
  SEARCH_DEMO_QUERY,
  TYPEWRITER_URL_EXAMPLES,
} from './constants';
import { useTypewriterCycle } from './useTypewriterCycle';
import {
  getScrapingUrlValidationMessage,
  validateScrapingUrl,
} from './validateScrapingUrl';

import iconArrowRightSrc from '~/icons/arrow-right.svg';

const LoadingSteps = dynamic(() =>
  import('./LoadingSteps').then((mod) => ({ default: mod.LoadingSteps }))
);
const ScrapingHeroResult = dynamic(() =>
  import('./ScrapingHeroResult').then((mod) => ({
    default: mod.ScrapingHeroResult,
  }))
);

type CardState = 'idle' | 'loading' | 'result';

const SCROLL_INTO_VIEW_DELAY_MS = 300;

const tabDisabledClassName =
  'pointer-events-none border border-gray-dark-700 bg-gray-dark-700';

function scrollInputToEnd(input: HTMLInputElement | null) {
  if (!input) return;
  input.scrollLeft = input.scrollWidth;
}

export function ScrapingHeroCard({
  tryExampleLabel,
  submitButton,
  phTracker,
  resultOverlayHeading,
  resultOverlayDescription,
  resultOverlayCtaText,
  resultOverlayCtaUrl,
  resultOverlayCtaPhTracker,
  classNames,
}: ScrapingCardProps) {
  const { texts } = useTranslation();

  const [state, setState] = useState<CardState>('idle');
  const [activeTab, setActiveTab] = useState<ScrapingHeroAction>('scrape');
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [focusPlaceholder, setFocusPlaceholder] = useState('');
  const [response, setResponse] = useState<ScrapingHeroResponse | null>(null);
  const [submittedProduct, setSubmittedProduct] =
    useState<ScrapingHeroAction>('scrape');
  const [request, setRequest] = useState<Promise<ScrapingHeroResponse> | null>(
    null
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const typewriterRef = useRef<HTMLSpanElement>(null);
  const typewriterTrackRef = useRef<HTMLDivElement>(null);

  const scrapeMetaRef = useRef<{
    targetUrl: string;
    isPlaceholder: boolean;
    selectedTab: ScrapingHeroAction;
  } | null>(null);
  const showIdlePlaceholder = state === 'idle' && !url && !isFocused;

  const showTypewriter = showIdlePlaceholder && activeTab !== 'search';
  const showStaticSearchPlaceholder =
    showIdlePlaceholder && activeTab === 'search';

  const currentPhraseRef = useTypewriterCycle(
    TYPEWRITER_URL_EXAMPLES,
    typewriterRef,
    showTypewriter,
    typewriterTrackRef
  );

  const scrollInputIntoView = useCallback(() => {
    const target = inputWrapperRef.current ?? inputRef.current;
    if (!target) return;

    window.setTimeout(() => {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, SCROLL_INTO_VIEW_DELAY_MS);
  }, []);

  useEffect(() => {
    scrollInputToEnd(inputRef.current);
  }, [url]);

  useEffect(() => {
    if (!isFocused) return;

    scrollInputIntoView();

    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleViewportChange = () => scrollInputIntoView();
    viewport.addEventListener('resize', handleViewportChange);
    viewport.addEventListener('scroll', handleViewportChange);

    return () => {
      viewport.removeEventListener('resize', handleViewportChange);
      viewport.removeEventListener('scroll', handleViewportChange);
    };
  }, [isFocused, scrollInputIntoView]);

  const tabLabels: Record<(typeof SCRAPING_HERO_TAB_IDS)[number], string> = {
    scrape: texts.scraping_hero.tab_scrape,
    search: texts.scraping_hero.tab_search,
    map: texts.scraping_hero.tab_map,
    crawl: texts.scraping_hero.tab_crawl,
  };

  const trackScrapeJobStarted = useCallback(
    (targetUrl: string, isPlaceholder: boolean) => {
      scrapeMetaRef.current = {
        targetUrl,
        isPlaceholder,
        selectedTab: activeTab,
      };

      try {
        track(
          submitButton?.phTracker ??
            phTracker ??
            SCRAPING_HERO_SCRAPE_JOB_STARTED_EVENT,
          {
            targetUrl,
            isPlaceholder,
            selectedTab: activeTab,
          }
        );
      } catch {}
    },
    [activeTab, phTracker, submitButton]
  );

  const trackScrapeOutcome = useCallback((event: string) => {
    const meta = scrapeMetaRef.current;
    if (!meta) return;

    sendGAEvent({
      event,
      sectionIdentifier: SECTION_NAMES.SCRAPING_HERO,
      targetUrl: meta.targetUrl,
      isPlaceholder: meta.isPlaceholder,
      selectedTab: meta.selectedTab,
      path: window.location.pathname,
    });
  }, []);

  const handleSubmit = useCallback(() => {
    setError('');

    if (!url.trim()) {
      const demoUrl =
        activeTab === 'search' ? SEARCH_DEMO_QUERY : currentPhraseRef.current;
      trackScrapeJobStarted(demoUrl, true);
      setUrl(demoUrl);
      setSubmittedProduct(activeTab);
      setState('loading');
      setRequest(scrape(demoUrl, activeTab, texts));
      return;
    }

    // search takes a free-text query, not a url
    if (activeTab === 'search') {
      const query = url.trim();
      setUrl(query);
      trackScrapeJobStarted(query, false);
      setSubmittedProduct(activeTab);
      setState('loading');
      setRequest(scrape(query, activeTab, texts));
      return;
    }

    const validation = validateScrapingUrl(url);
    if (!validation.ok) {
      setError(
        getScrapingUrlValidationMessage(validation.reason, texts.scraping_hero)
      );
      return;
    }

    setUrl(validation.url);
    trackScrapeJobStarted(validation.url, false);
    setSubmittedProduct(activeTab);
    setState('loading');
    setRequest(scrape(validation.url, activeTab, texts));
  }, [url, texts, activeTab, currentPhraseRef, trackScrapeJobStarted]);

  const handleLoadingComplete = useCallback(
    (data: ScrapingHeroResponse) => {
      trackScrapeOutcome(SCRAPING_HERO_SCRAPE_SUCCEEDED_EVENT);
      setRequest(null);
      setResponse(data);
      setState('result');
    },
    [trackScrapeOutcome]
  );

  const handleLoadingError = useCallback(
    (message?: string) => {
      trackScrapeOutcome(SCRAPING_HERO_SCRAPE_FAILED_EVENT);
      setRequest(null);
      setState('idle');
      setError(message ?? texts.scraping_hero.request_failed);
    },
    [texts.scraping_hero.request_failed, trackScrapeOutcome]
  );

  const isExpanded = state !== 'idle';
  const isInputDisabled = state !== 'idle';
  const isResultVisible = state === 'result';

  const cardOuterClassName = cn(
    'scraping-hero-card-outer',
    'relative mx-auto flex w-full flex-col items-center justify-center md:rounded-t-3xl',
    'p-4 lg:px-15 lg:py-15 md:backdrop-blur-[30px]',
    'max-md:border max-md:border-gray-100/40',
    'md:max-w-[590px]',
    isExpanded ? 'lg:max-w-[1100px]' : 'lg:max-w-4xl',
    gradientDecodoBorderStyling({
      borderWidth: 'p-px',
      borderRadius: 'rounded-3xl',
      color: 'gradient-secondary-5',
    }),
    'before:hidden md:before:block',
    classNames?.root
  );

  const cardClassName = cn(
    'scraping-hero-card',
    'relative m-auto flex w-full flex-col items-start rounded-3xl',
    isExpanded ? 'scraping-hero-card-expanded max-w-[700px]' : 'max-w-[630px]',
    classNames?.card
  );

  const cardGrowSectionClassName = cn(
    'scraping-hero-card-grow-section',
    isExpanded && 'scraping-hero-card-grow-section--open'
  );

  const inputClassName = cn(
    'scraping-hero-input-track w-full min-w-0 border-none bg-transparent font-mono text-body-m text-base-dark-white outline-none',
    'overflow-x-auto whitespace-nowrap placeholder:text-gray-dark-400'
  );

  const submitButtonIcons = passButtonIcons({
    rightSection: submitButton?.rightSection,
    fallbackIcon: {
      rightSection: (
        <SvgIcon
          src={iconArrowRightSrc}
          className='h-4 w-4'
          aria-hidden='true'
        />
      ),
    },
  });

  const getTabClassName = (tabId: ScrapingHeroAction) => {
    if (isResultVisible) return tabDisabledClassName;
    return activeTab === tabId
      ? HERO_WIDGET_EXAMPLE_BADGE_SELECTED
      : HERO_WIDGET_EXAMPLE_BADGE;
  };

  const getTabTextClassName = (tabId: ScrapingHeroAction) => {
    const base = 'normal-case text-body-s font-normal font-serif';
    if (isResultVisible) return cn(base, 'text-gray-dark-500');

    return activeTab === tabId
      ? cn(base, 'text-base-dark-white')
      : cn(base, 'text-gray-dark-400');
  };

  return (
    <div className={cardOuterClassName}>
      <WidgetGlassOverlay
        className={cn('after:hidden!', classNames?.glassOverlay)}
      />
      <div className={cn('relative z-10 w-full', cardClassName)}>
        <div
          className={cn(
            'w-full overflow-hidden rounded-3xl',
            classNames?.cardInner
          )}
        >
          <div className='flex w-full flex-col gap-3 p-5'>
            <div className='flex flex-col gap-2'>
              {tryExampleLabel && (
                <p className='m-0 font-serif text-label-s font-semibold uppercase text-gray-dark-500'>
                  {tryExampleLabel}
                </p>
              )}

              <div className='flex flex-wrap gap-1.5'>
                {SCRAPING_HERO_TAB_IDS.map((tabId) => (
                  <Button
                    key={tabId}
                    noStyles
                    type='button'
                    disabled={isResultVisible}
                    data-tracker-type='tab'
                    data-tracker-component='badge'
                    data-tracker-context={tabId}
                    aria-pressed={activeTab === tabId}
                    onClick={() => setActiveTab(tabId)}
                    classNames={{ root: 'cursor-pointer' }}
                  >
                    <Badge
                      variant='filled'
                      color='brand'
                      classNames={{
                        root: getTabClassName(tabId),
                      }}
                    >
                      <Text
                        variant='body-s'
                        classNames={{ wrapper: getTabTextClassName(tabId) }}
                      >
                        {tabLabels[tabId]}
                      </Text>
                    </Badge>
                  </Button>
                ))}
              </div>
            </div>

            <div className='w-full border-t border-gray-dark-700' />

            <div className='flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between'>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <span className={HERO_WIDGET_ICON_WRAPPER}>
                  <ScrapersMediumIcon size={24} aria-hidden='true' />
                </span>
                <div
                  ref={inputWrapperRef}
                  className='relative flex h-8 min-w-0 flex-1 items-center overflow-hidden'
                >
                  <input
                    ref={inputRef}
                    type='text'
                    className={inputClassName}
                    placeholder={isFocused && !url ? focusPlaceholder : ''}
                    value={url}
                    disabled={isInputDisabled}
                    onChange={(event) => {
                      setUrl(event.target.value);
                      if (error) setError('');
                      scrollInputToEnd(event.currentTarget);
                    }}
                    onFocus={() => {
                      setFocusPlaceholder(
                        activeTab === 'search'
                          ? SEARCH_DEMO_QUERY
                          : currentPhraseRef.current
                      );
                      setIsFocused(true);
                      scrollInputIntoView();
                    }}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleSubmit();
                    }}
                    aria-label={texts.scraping_hero.input_aria_label}
                    aria-invalid={!!error}
                    aria-describedby={error ? 'scraping-hero-error' : undefined}
                    autoComplete='off'
                  />
                  {showTypewriter && (
                    <div
                      className='scraping-hero-typewriter pointer-events-none absolute inset-0 overflow-hidden'
                      aria-hidden='true'
                    >
                      <div
                        ref={typewriterTrackRef}
                        className='scraping-hero-input-track flex h-full min-w-0 items-center overflow-x-auto'
                      >
                        <span
                          ref={typewriterRef}
                          className='whitespace-nowrap font-mono text-body-m text-gray-dark-400'
                        />
                        <span className='scraping-hero-typewriter-cursor shrink-0 text-gray-dark-400'>
                          |
                        </span>
                      </div>
                    </div>
                  )}
                  {showStaticSearchPlaceholder && (
                    <div
                      className='pointer-events-none absolute inset-0 overflow-hidden'
                      aria-hidden='true'
                    >
                      <div className='scraping-hero-input-track flex h-full min-w-0 items-center'>
                        <span className='whitespace-nowrap font-mono text-body-m text-gray-dark-400'>
                          {SEARCH_DEMO_QUERY}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Button
                variant={submitButton?.variant ?? 'filled'}
                color={submitButton?.color ?? 'white'}
                size='s'
                classNames={{ root: 'w-full shrink-0 md:w-auto' }}
                {...submitButtonIcons}
                onClick={handleSubmit}
                disabled={isInputDisabled}
                aria-label={texts.scraping_hero.submit_aria_label}
              >
                {submitButton?.text || texts.scraping_hero.submit_aria_label}
              </Button>
            </div>

            {error && (
              <p
                id='scraping-hero-error'
                className='font-mono text-body-s text-error-500'
                role='alert'
              >
                {error}
              </p>
            )}
          </div>

          <div className={cardGrowSectionClassName}>
            <div className='scraping-hero-card-grow-section-inner'>
              {state === 'loading' && request && (
                <div className='scraping-hero-stage-enter'>
                  <LoadingSteps
                    request={request}
                    onComplete={handleLoadingComplete}
                    onError={handleLoadingError}
                  />
                </div>
              )}

              {state === 'result' && response && (
                <div className='scraping-hero-stage-enter p-5'>
                  <ScrapingHeroResult
                    response={response}
                    product={submittedProduct}
                    resultOverlayHeading={resultOverlayHeading}
                    resultOverlayDescription={resultOverlayDescription}
                    resultOverlayCtaText={resultOverlayCtaText}
                    resultOverlayCtaUrl={resultOverlayCtaUrl}
                    resultOverlayCtaPhTracker={resultOverlayCtaPhTracker}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
